// Merged exams EmptyState (optional JSX icon in a bg-base-200 tile, semantic classes) + libms EmptyState (compact prop, description, action slot); libms's hardcoded blue tile rgba(37,99,235,0.08) replaced by base-200, tile radius → rounded-800 (see the note at the tile).
//
// TWO MODES (2026-09-17, audit UX-09). Three visual variants of "empty" had
// grown across the screens and the copy was wrong for first run: an empty
// roster with no filters set said "No patrons match these filters — clear the
// filters", telling the operator to clear filters they never applied. The
// distinction is not the caller's prose to get right each time; it is a MODE:
//
//   nothing-yet   the collection is genuinely empty — "No patrons yet", the
//                 hint on what fills it, and the one accent CTA that creates the
//                 first record ("Add patron").
//   no-matches    a filter or search excluded everything — "No patrons match
//                 these filters" and the way BACK TO RESULTS: a "Clear filters"
//                 button (`onClearFilters`, usually `updateSearch(navigate, …)`
//                 from @erp/ui/route-kit). Never a create button here: the
//                 records exist, the reader just cannot see them.
//
// DataTable picks the mode from `hasFilters`; a card list passes it directly.
// `title` / `description` remain as the free-form override for a region whose
// emptiness is neither ("No fines outstanding for this member").
//
// THE TILE CAN WEAR THE MODULE HUE (2026-09-22, "every page helps" lane L2,
// contract item d). The primary-tint tile is the default — an invitation to
// act — but an empty register inside a console reads as part of THAT console
// when its tile wears the identity hue the page heading already carries
// (`hue={MODULE_HUE.patrons}`), the same two-tone pair IconCapsule paints.
// Identity, never status: the hue says which area is empty, not that emptiness
// is good or bad. A hue is a REQUEST FOR THE TILE: name one with no icon and no
// mode and the glyph falls back to the nothing-yet one, because a hue that
// paints nothing is a silent no-op (2026-09-22). With no hue, free-form keeps
// its honest tile-less floor.
import { Show, createMemo, type Component, type JSX } from 'solid-js';
import { Dynamic } from 'solid-js/web';
import { Link } from '@tanstack/solid-router';
import { Inbox, SearchX } from 'lucide-solid';
import { Button, buttonClasses } from '../button/index.js';
import { huePair, type CategoryHue } from '../category-hue/index.js';

/** Why the region is empty — decides the copy and the one button it offers. */
export type EmptyStateMode = 'nothing-yet' | 'no-matches';

/**
 * The object form of `action`: the CTA that creates the first record. `to`
 * renders a router Link wearing the button recipe (one element, one tab
 * stop); `onClick` renders a Button. One of the two.
 */
export interface EmptyStateAction {
  /** The verb, sentence case — "Add patron", "Import MARC". */
  label: string;
  /** What the button does. */
  onClick?: () => void;
  /** A route to go to instead — the create form. */
  to?: string;
  /** Automation id on the button / link. */
  id: string;
}

type IconInput = JSX.Element | Component<{ size?: number; class?: string }>;

const isActionSpec = (a: unknown): a is EmptyStateAction =>
  typeof a === 'object' && a !== null && typeof (a as EmptyStateAction).label === 'string';

/**
 * What a list, table or panel shows when it has nothing to show. Two modes
 * (`nothing-yet` with a create CTA, `no-matches` with Clear filters) chosen by
 * whether a filter is applied; `title` / `description` for a region that is
 * neither. DataTable and ListView render it for you; use it directly in
 * panels and card lists.
 */
export function EmptyState(props: {
  /**
   * `nothing-yet` — the collection is empty; `no-matches` — the filters excluded
   * everything. Sets the default title, icon and button. Omit for a free-form
   * empty state described by `title`.
   */
  mode?: EmptyStateMode;
  /** The plural noun the mode copy is about — "patrons" → "No patrons yet" / "No patrons match these filters". */
  what?: string;
  /** The headline, in sentence case: "No loans yet" / "No results for 'kafka'". Overrides the mode copy. Never the word "Empty". */
  title?: string;
  /** One or two sentences on why it is empty or what to do next; wraps at ~380px. */
  description?: string;
  /** A lucide icon — an ELEMENT (`<Users size={24} />`) or the COMPONENT (`Users`) — in the primary-tint tile above the title. A mode brings its own default. Decorative. */
  icon?: IconInput;
  /**
   * The module's hue from `MODULE_HUE`: the icon tile wears the identity two-tone
   * pair (`bg-<hue>-tint text-<hue>-ink`) instead of the primary tint, so an empty
   * register reads as part of the console whose heading carries the same capsule.
   * Identity, never status. `neutral` gives the gray body. Omitted, the tile
   * keeps the primary tint.
   *
   * Naming a hue GUARANTEES the tile: with no `icon` and no `mode` the glyph
   * falls back to the nothing-yet one rather than dropping the tile the hue was
   * meant to paint. Pass the register's own glyph anyway — the fallback is a
   * floor, not a design.
   */
  hue?: CategoryHue | 'neutral';
  /**
   * The way out in `nothing-yet` (and free-form) mode — a `{ label, onClick | to, id }`
   * spec rendered as the accent button, or your own element. Not rendered in
   * `no-matches`: the escape hatch there is Clear filters, never a create.
   */
  action?: JSX.Element | EmptyStateAction;
  /** `no-matches` only: reset the URL search — `() => updateSearch(navigate, () => ({}))`. Renders the "Clear filters" button (`${id}-clear-filters`). */
  onClearFilters?: () => void;
  /** Tighter padding and a smaller tile for an empty state inside a panel or card rather than a whole page. */
  compact?: boolean;
  /** Automation id on the wrapper; the buttons are `${id}-clear-filters` and the action spec's own id. */
  id?: string;
  /** Extra classes on the wrapper — height or alignment inside a custom container. */
  class?: string;
}) {
  const what = () => props.what ?? 'records';
  const title = () =>
    props.title ??
    (props.mode === 'no-matches' ? `No ${what()} match these filters` : props.mode === 'nothing-yet' ? `No ${what()} yet` : 'No records found.');

  // Resolved ONCE per change: an icon ELEMENT is a getter that builds DOM on
  // every read, so the function test and the render share one read. A
  // component (a lucide icon) is mounted with the tile's glyph size; a mode
  // with no icon takes its own.
  const icon = createMemo<JSX.Element>(() => {
    const i = props.icon;
    const size = props.compact ? 18 : 22;
    if (typeof i === 'function') return <Dynamic component={i as Component<{ size?: number }>} size={size} />;
    if (i !== undefined) return i;
    if (props.mode === 'no-matches') return <SearchX size={size} />;
    if (props.mode === 'nothing-yet') return <Inbox size={size} />;
    // A HUE IS A REQUEST FOR THE TILE (2026-09-22). The hue only ever paints
    // the tile, so a caller that named one but no glyph got NOTHING — silently,
    // on 57 of the 67 list screens the identity sweep had just wired. Naming a
    // hue now guarantees the tile, falling back to the nothing-yet glyph. It is
    // a FLOOR, not a design: name the register's own glyph and it never fires.
    // A free-form state with no hue keeps the honest tile-less floor.
    if (props.hue) return <Inbox size={size} />;
    return undefined;
  });

  const action = createMemo(() => props.action);

  return (
    <div
      id={props.id}
      data-mode={props.mode}
      class={`flex flex-col items-center justify-center gap-3 text-center ${
        props.compact ? 'px-4 py-10' : 'px-6 py-16'
      } ${props.class ?? ''}`}
    >
      <Show when={icon()}>
        {/* The source's tile: a PRIMARY-tinted rounded square with a primary
            glyph — a grey tile is what made mini's empty states read dead.
            rounded-800: the source's 14px is not a Spectrum corner step (the
            scale jumps 10 -> 16); the tile takes the nearest, corner-radius-800. */}
        <div
          class={`grid shrink-0 place-items-center rounded-800 ${props.hue ? huePair(props.hue) : 'bg-primary-tint text-primary'} ${
            props.compact ? 'h-11 w-11' : 'h-14 w-14'
          }`}
          aria-hidden="true"
        >
          {icon()}
        </div>
      </Show>
      <div>
        <h3 class="text-base">{title()}</h3>
        <Show when={props.description}>
          <p class="mx-auto mt-1 max-w-[380px] text-xs leading-relaxed text-base-subtle">
            {props.description}
          </p>
        </Show>
      </div>
      <Show
        when={props.mode === 'no-matches'}
        fallback={
          <Show when={action()}>
            {(a) => (
              <Show when={isActionSpec(a())} fallback={a() as JSX.Element}>
                {/* The CTA that creates the first record — the accent. A route
                    renders as a router Link in the button recipe (one element,
                    one tab stop); a handler as a Button. */}
                <Show
                  when={(a() as EmptyStateAction).to}
                  fallback={
                    <Button id={(a() as EmptyStateAction).id} variant="primary" onClick={() => (a() as EmptyStateAction).onClick?.()}>
                      {(a() as EmptyStateAction).label}
                    </Button>
                  }
                >
                  {(to) => (
                    <Link id={(a() as EmptyStateAction).id} to={to()} class={buttonClasses({ variant: 'primary' })} data-variant="primary">
                      {(a() as EmptyStateAction).label}
                    </Link>
                  )}
                </Show>
              </Show>
            )}
          </Show>
        }
      >
        <Show when={props.onClearFilters}>
          {(clear) => (
            /* Secondary, not the accent: clearing filters is the way back, not
               the action the screen is for. */
            <Button id={props.id ? `${props.id}-clear-filters` : undefined} variant="secondary" onClick={() => clear()()}>
              Clear filters
            </Button>
          )}
        </Show>
      </Show>
    </div>
  );
}
